import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faCheckCircle, faSpinner, faInfoCircle, faCircle } from "@fortawesome/free-solid-svg-icons"; // Added icons

const ViewVacatePage = () => {
  const [vacateData, setVacateData] = useState([]);
  const [loading, setLoading] = useState(true); // Add loading state

  // Function to fetch vacate data (moved out for reusability)
  const fetchVacateData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const userDataString = localStorage.getItem("user");
    let userId = null;

    try {
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        userId = userData.unique;
      }

      if (!token || !userId) {
        toast.warn("Authentication details missing. Please log in.");
        setVacateData([]); // Clear data if not authenticated
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `https://backend.inyumba.co.ke/iNyumba/vacatinghouse/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Ensure response.data.response is an array, or wrap it
      setVacateData(Array.isArray(response.data.response) ? response.data.response : [response.data.response].filter(Boolean));
    } catch (error) {
      console.error("Error fetching vacate data:", error);
      setVacateData([]); // Clear data on error
      if (error.response && error.response.data && error.response.data.response) {
        toast.error(error.response.data.response);
      } else {
        toast.error("Failed to fetch vacate information.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVacateData();
  }, []); // Empty dependency array means this runs once on mount

  const handleCompleteVacating = async (vacatingId) => {
    if (!window.confirm("Are you sure you want to complete the vacating process? This action cannot be undone.")) {
      return;
    }

    const token = localStorage.getItem("token");
    const userDataString = localStorage.getItem("user");
    let userId = null;

    try {
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        userId = userData.unique;
      }

      if (!token || !userId) {
        toast.error("Authentication details missing. Please log in again.");
        return;
      }

      const response = await axios.put(
        `https://backend.inyumba.co.ke/iNyumba/vacatinghouse/${vacatingId}/user/${userId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response || "Vacating process completed successfully!");
      fetchVacateData(); // Refresh data after completion
    } catch (error) {
      console.error("Error completing vacating:", error);
      if (error.response && error.response.data && error.response.data.response) {
        toast.error(error.response.data.response);
      } else {
        toast.error("An error occurred while completing the vacating process.");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Complete":
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "Pending":
      case "AWAITING_REPAIRS":
        return "bg-yellow-100 text-yellow-800";
      case "ALLOW":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-indigo-50 to-purple-100 p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-4xl w-full py-8 text-center animate-fade-in">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faHome} className="text-purple-500 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Vacate Status & Details
            </h1>
          </div>
          <p className="text-gray-600 text-lg mb-10">
            Review the progress and details of your property vacation process.
          </p>

          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl flex flex-col items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} spin className="text-indigo-500 text-5xl mb-4" />
              <p>Loading vacate information...</p>
            </div>
          ) : vacateData && vacateData.length > 0 ? (
            vacateData.map((vacateRecord, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 space-y-6 mb-8 text-left"
              >
                <h2 className="text-3xl font-semibold text-gray-800 border-b-2 border-purple-200 pb-4 mb-6">
                  Vacate Information Overview
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {/* Financial Details */}
                  <div className="space-y-3">
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">Total Amount for Repairs:</span>{" "}
                      <span className="font-bold text-gray-900 text-xl">
                        KSh {parseFloat(vacateRecord.totalamountrepairs || 0).toLocaleString()}
                      </span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">Service Fee:</span>{" "}
                      <span className="font-bold text-gray-900 text-xl">
                        KSh {parseFloat(vacateRecord.servicefee || 0).toLocaleString()}
                      </span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">Total Fee:</span>{" "}
                      <span className="font-bold text-gray-900 text-xl">
                        KSh {parseFloat(vacateRecord.totalfee || 0).toLocaleString()}
                      </span>
                    </p>
                  </div>
                  {/* Status Details */}
                  <div className="space-y-3">
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">Repair Charges:</span>{" "}
                      <span className="font-bold text-gray-900 text-xl">
                        KSh {parseFloat(vacateRecord.repaircharges || 0).toLocaleString()}
                      </span>
                    </p>
                    <p className="text-lg flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Completion Status:</span>{" "}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(vacateRecord.completestatus)}`}>
                        <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                        {vacateRecord.completestatus}
                      </span>
                    </p>
                    <p className="text-lg flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Your Vacate Status:</span>{" "}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(vacateRecord.vacatingstatus)}`}>
                        <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                        {vacateRecord.vacatingstatus}
                      </span>
                    </p>
                  </div>
                </div>
                {vacateRecord.vacatingstatus === "ALLOW" && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => handleCompleteVacating(vacateRecord.vacatinghouse_id)}
                      className="flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-xl text-xl font-bold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Click to Complete Vacating</span>
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-5xl mb-4" />
              <p>No vacate information available for your account at this time.</p>
              <p className="mt-2 text-lg">Please initiate a vacate request if needed.</p>
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default ViewVacatePage;